<template>
    <div class="scc-container scc-container__column alternate-registration-form">
        <article class="alternate-registration-form__left">
            <div class="alternate-registration-form__background" />
            <div
                class="alternate-registration-form__info"
                :class="{ 'alternate-registration-form__info--extra-padding': themeLogo }"
            >
                <div v-if="themeLogo" class="alternate-registration-form__logo-container">
                    <img
                        :src="themeLogo | cdn({ width: 500, height: 500 })"
                        class="alternate-registration-form__logo"
                    />
                </div>
                <h1 class="alternate-registration-form__heading">Steun {{ game.name }}</h1>
                <p class="alternate-registration-form__description">{{ fullDescription }}</p>
                <SccLink class="alternate-registration-form__link" :to="link">
                    {{ linkText }}
                </SccLink>
            </div>
            <article class="alternate-registration-form__prize-wrapper">
                <div class="alternate-registration-form__prize">
                    <h2 class="alternate-registration-form__prize-heading">Hoofdprijs</h2>
                    <p class="alternate-registration-form__prize-block">
                        <span class="alternate-registration-form__amount">€5.000,-</span>
                        <span class="alternate-registration-form__detail">Voor jou</span>
                    </p>
                    <p class="alternate-registration-form__spacer">/</p>
                    <p class="alternate-registration-form__prize-block">
                        <span class="alternate-registration-form__amount">€5.000,-</span>
                        <span class="alternate-registration-form__detail">Voor je club</span>
                    </p>
                </div>
            </article>
        </article>
        <article class="alternate-registration-form__right">
            <ContentBlock v-if="ticketSaleOpen">
                <h2 class="alternate-registration-form__title">
                    {{ title }}{{ affiliate ? ' voor ' + affiliate.name : '' }}
                </h2>

                <ValidationObserver v-slot="{ handleSubmit }" slim>
                    <form :name="formName" class="track-form" novalidate @submit.prevent="handleSubmit(onSubmit)">
                        <AlternateFormName
                            v-model="form.name"
                            is-showing-first-name
                            is-requiring-first-name
                            is-requiring-last-name
                        />
                        <AlternateFormEmail v-model="form.email" />
                        <AlternateFormAge v-model="form.birthday" label="Geboortedatum (dd-mm-jjjj)" required />
                        <span class="mb-1 alternate-registration-form__subtitle">Aantal loten</span>
                        <AlternateFormTicketAmount v-model="form.tickets" />
                        <span v-if="currentPaymentMethods.length > 1" class="mb-1 alternate-registration-form__subtitle"
                            >Betaalmethode</span
                        >
                        <AlternateFormPayment v-model="form.paymentMethod" :options="currentPaymentMethods" />
                        <AlternateFormCheckbox
                            v-model="form.optInTerms"
                            name="optInTerms"
                            :label="optInTermsText"
                            :info="optInTermsInfo"
                            :gameRulesLink="rulesLink"
                            :required="true"
                            class="mb-1"
                        />
                        <AlternateFormCheckbox
                            v-model="form.optInMarketing"
                            name="optInMarketing"
                            :label="optInMarketingText"
                            :info="optInMarketingInfo"
                        />
                        <small class="alternate-registration-form__marketing-message">
                            Voor meer informatie zie:
                            <a href="https://www.knvb.nl/info/3043/privacy" target="_blank">
                                https://www.knvb.nl/info/3043/privacy
                            </a>
                        </small>
                        <button
                            class="alternate-registration-form__button"
                            :class="loading ? 'alternate-registration-form__button--loading' : null"
                            type="submit"
                            :disabled="isDisabled"
                        >
                            <span class="alternate-registration-form__button-text">{{ buttonContent }}</span>
                            <ArrowRightIcon v-if="!loading" class="alternate-registration-form__button-icon" />
                            <Spinner v-if="loading" color="secondary-contrast" />
                        </button>
                    </form>
                </ValidationObserver>
            </ContentBlock>
            <ContentBlock v-else class="alternate-registration-form__drawing-closed">
                <h2 class="alternate-registration-form__title">
                    Verkoop is gesloten!
                </h2>
                <p>
                    Helaas is de lotenverkoop gesloten. Kijk hier of de uitslag al bekend is!
                </p>
                <SccLink to="trekkingsuitslag" class="alternate-registration-form__button">
                    <span class="alternate-registration-form__button-text">Bekijk trekkingsuitslag</span>
                    <ArrowRightIcon class="alternate-registration-form__button-icon" />
                </SccLink>
            </ContentBlock>
        </article>
    </div>
</template>

<script>
import './alternate-registration-form.scss';

import { ValidationObserver } from 'vee-validate';

import { stripChars } from './../../utils/string';
import { fromStorage } from './../../utils/storage';
import { ticketSaleOpen } from './../../utils/data-conversions';
import ArrowRightIcon from '../../assets/icons/arrow-right';

import AlternateFormName from './AlternateFormName';
import AlternateFormCheckbox from './AlternateFormCheckbox';
import AlternateFormAge from './AlternateFormAge';
import AlternateFormEmail from './AlternateFormEmail';
import AlternateFormTicketAmount from './AlternateFormTicketAmount';
import AlternateFormPayment from './AlternateFormPayment';
import Spinner from '../global/Spinner';
import ContentBlock from '../global/ContentBlock';
import SccLink from '../global/SccLink';

export default {
    name: 'AlternateRegistrationForm',
    components: {
        ValidationObserver,
        ArrowRightIcon,
        AlternateFormName,
        AlternateFormAge,
        AlternateFormEmail,
        AlternateFormTicketAmount,
        AlternateFormPayment,
        AlternateFormCheckbox,
        Spinner,
        ContentBlock,
        SccLink,
    },
    __editor: {
        requiredData: ['game', 'salesChannel', 'themeSettings'],
        hidden: ['onBehalfOf'],
        fields: {
            description: {
                widget: 'textarea',
            },
        },
    },
    props: {
        themeSettings: {
            type: Object,
        },
        title: {
            type: String,
            default: 'Loten kopen',
        },
        buttonText: {
            type: String,
            default: 'Afrekenen',
        },
        game: {
            type: Object,
            required: true,
        },
        salesChannel: {
            type: Object,
            required: true,
        },
        description: {
            type: String,
            default: 'Voor € 2,- per lot steun {{ name }} én maak je kans op te gekke prijzen.',
        },
        link: {
            type: String,
            default: '#prizes',
        },
        linkText: {
            type: String,
            default: 'Bekijk het prijzenpakket',
        },
        onBehalfOf: {
            type: String,
            default: 'current',
        },
        optInTermsText: {
            type: String,
            default: 'Ik ga akkoord met de spelregels en het privacybeleid.',
        },
        optInTermsInfo: {
            type: String,
            default: '',
        },
        optInMarketingText: {
            type: String,
            default:
                'Ik wil graag weten of er aanbiedingen zijn van <a href="https://www.knvb.nl/over-ons/partners" target="_blank">partners</a> van de KNVB of van partijen uit geselecteerde branches. Vanzelfsprekend kun je je op ieder moment afmelden.',
        },
        optInMarketingInfo: {
            type: String,
            default:
                "We doen natuurlijk ons uiterste best om je alleen passende aanbiedingen te sturen. Hiervoor ontvangen we geld van onze partners. Maar geen zorgen, we gaan d'r niet van op vakantie, maar investeren deze inkomsten weer in het amateurvoetbal. Samen maken we ons voetbal steeds beter! Vanzelfsprekend kun je je op ieder moment afmelden.",
        },
    },
    data() {
        return {
            formName: 'loterij',
            ticketPrice: this.game.ticketPrice,
            submitted: false,
            loading: false,
            zipCodeLookup: true,
            form: {
                tickets: 2,
                subscriptionType: 'quarterly',
                gender: null,
                name: {
                    firstName: '',
                    lastNamePrefix: '',
                    lastName: '',
                },
                email: '',
                emailConfirm: '',
                phone: null,
                address: {
                    street: null,
                    houseNr: null,
                    houseNrAddition: null,
                    zipCode: null,
                    city: null,
                    country: null,
                },
                paymentMethod: undefined,
                birthday: null,
                optInTerms: false,
                optInMarketing: false,
            },
            affiliate: undefined,
        };
    },
    computed: {
        themeLogo() {
            return this.themeSettings?.logo || '';
        },
        ticketSaleOpen() {
            return ticketSaleOpen(this.game);
        },
        rulesLink() {
            return window.location.origin + '/spelreglement';
        },
        fullDescription() {
            return this.description.replace(/{{[^}]+}}/, this.game.name);
        },
        currentPaymentMethods() {
            return this.salesChannel?.paymentMethods?.map(method => method.type) || [];
        },
        currentTotal() {
            return Number.isNaN(this.form.tickets)
                ? '€0.00'
                : (this.form.tickets * this.ticketPrice).toLocaleString('en-NL', {
                      style: 'currency',
                      currency: 'EUR',
                  });
        },
        buttonContent() {
            if (this.isDisabled && !this.loading) return 'Er is geen betaalmethode gekozen!';
            else
                return `${this.buttonText}${this.form.paymentMethod ? ' met ' + this.form.paymentMethod : ''} | ${
                    this.currentTotal
                }`;
        },
        isDisabled() {
            return this.loading || (!this.isChanceGame() && this.currentPaymentMethods.length === 0);
        },
        redirectUrl() {
            let baseUrl = window.location.protocol + '//' + window.location.host + this.$router.options.base;
            let thanksPath = baseUrl.endsWith('/') ? 'bedankt' : '/bedankt';
            return baseUrl + thanksPath;
        },
        affiliateId() {
            return fromStorage('affiliate');
        },
    },
    created() {
        if (this.affiliateId) this.getAffiliate();
    },
    methods: {
        getAffiliate() {
            this.$api
                ?.getAffiliate(this.affiliateId)
                ?.then(affiliate => {
                    if (!affiliate) {
                        this.affiliate = undefined;
                    } else {
                        this.affiliate = { ...affiliate, id: this.affiliateId };
                    }
                })
                ?.catch(() => {
                    this.error = true;
                });
        },
        createOrderError(err) {
            if (err.includes('not possible to issue tickets') || err.includes('not published'))
                this.$toast.error('Ah wat jammer! Je kunt op dit moment geen loten kopen.');
            else if (err.includes('tickets left')) this.$toast.error('Ongelooflijk, de loten zijn uitverkocht!');
            else
                this.$toast.error(
                    'Er is iets mis gegaan. Geen zorgen, er is nog niks van je rekening afschreven. Probeer het opnieuw.'
                );
        },
        formatCurrency(value) {
            if (['number', 'string'].includes(typeof value)) {
                let returnValue = parseFloat(value);
                returnValue = returnValue.toFixed(2);

                return returnValue.replace('.', ',');
            }
        },
        isChanceGame() {
            const whitelist = ['chance', 'small_chance', 'club_link'];
            return whitelist.includes(this.game.type);
        },
        onSubmit() {
            this.submitted = true;

            const {
                tickets,
                gender,
                name,
                email,
                phone,
                address,
                paymentMethod,
                cardNumber,
                birthday,
                optInTerms,
                optInMarketing,
            } = this.form;

            const customer = {
                gender,
                ...name,
                email,
                phone: stripChars(phone, '+-()'),
                ...address,
                cardNumber,
                birthday,
                optInAge: optInTerms, // Opt-in age is combined with opt-in terms
                optInTerms,
                optInMarketing,
            };

            const input = {
                customer,
                tickets,
                channel: process.env.GRIDSOME_CHANNEL,
                paymentMethod,
                redirectUrl: this.redirectUrl,
                reference: fromStorage('reference'),
                affiliateId: this.affiliateId,
                subGameId: this.game?.subGameId || null,
            };

            if (paymentMethod === 'sepa') {
                if (process.env.VUE_APP_ENV === 'prod') {
                    let now = new Date();
                    input['mandateAcceptedDate'] = now.toISOString();
                }

                return this.createGameSubscription(input);
            }

            this.createOrder(input);
        },
        async createOrder(formInput) {
            this.loading = true;
            this.$api
                ?.createOrder(formInput, this.onBehalfOf)
                .then(response => {
                    if (response.paymentUrl) window.location = response.paymentUrl;
                })
                .catch(error => {
                    this.createOrderError(error);
                    this.loading = false;
                });
        },
        async createGameSubscription(formInput) {
            this.loading = true;
            await this.$api
                .createGameSubscription(formInput, this.onBehalfOf)
                .then(createGameSubscription => {
                    if (
                        createGameSubscription.gameSubscription.status !== 'active' &&
                        createGameSubscription.paymentUrl
                    ) {
                        window.location = createGameSubscription.paymentUrl;
                        return;
                    }

                    this.$router.push({
                        name: 'Page',
                        params: { path: 'bedankt' },
                        query: {
                            game_subscription_id: createGameSubscription.gameSubscription.id,
                        },
                    });
                })
                .catch(error => {
                    this.createOrderError(error);
                    this.loading = false;
                });
            this.loading = false;
        },
    },
};
</script>
